import {
  SCOPED_CATEGORY,
  SCOPED_COLLECTION,
  SCOPED_PRODUCT_TYPE,
  SCOPED_VENDOR,
  scopedFilterParams,
} from '@constants/scopedSuggestion';
import type { ScopedFilter } from '@providers/FilterProvider';
import { useFilterSettings, useFilterState } from '@providers/FilterProvider';
import { useCallback, useMemo } from 'react';

export type UseFilterOptionItemProps = {
  filterOptionId: string;
  value: string;
  handle?: string;
  items?: Array<Pick<UseFilterOptionItemProps, 'value' | 'items'>>;
  filterType?: unknown;
};

const filterOptionHasChildSelected = (
  selectOptionItems: string[],
  items: UseFilterOptionItemProps['items']
): boolean => {
  return !!items?.some(({ value, items }) => {
    if (selectOptionItems.includes(value)) return true;

    return items ? filterOptionHasChildSelected(selectOptionItems, items) : false;
  });
};

export const useFilterOptionItem = ({
  value,
  filterOptionId,
  items,
  filterType,
}: UseFilterOptionItemProps) => {
  const { filterParams, toggleFilterParams } = useFilterState();
  const { showOutOfStockOption, showFilterOptionCount } = useFilterSettings();
  const isOptionItemSelected = useMemo(() => {
    let filterParamsByOptionId = filterParams?.[filterOptionId];

    if (filterType && !filterParamsByOptionId) {
      [SCOPED_VENDOR, SCOPED_PRODUCT_TYPE, SCOPED_CATEGORY, SCOPED_COLLECTION].forEach(
        (scopedType) => {
          if (filterType === scopedType) {
            filterParamsByOptionId = scopedFilterParams[scopedType as ScopedFilter];
            return;
          }
        }
      );
    }

    if (!filterParamsByOptionId) return false;

    const itemSelected =
      filterParams?.[filterOptionId]?.includes(value) ||
      filterParams?.[filterParamsByOptionId]?.includes(value);
    if (itemSelected) return true;

    return items ? filterOptionHasChildSelected(filterParamsByOptionId, items) : false;
  }, [filterParams?.[filterOptionId], value, items, filterParams]);

  const handleOptionItemSelect = useCallback(() => {
    toggleFilterParams(filterOptionId, value);
  }, [value, toggleFilterParams, filterOptionId]);

  return {
    isOptionItemSelected,
    handleOptionItemSelect,
    showOutOfStockOption,
    showFilterOptionCount,
  };
};
